import { z } from "zod";

const publicUrl = z.string().url().refine((value) => {
  const url = new URL(value);
  return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password;
});
/** 외부 원본 응답이나 공급자 식별자를 허용하지 않는 후보 조회 계약입니다. */
export const candidateValuesSchema = z.object({
  title: z.string().nullable(), address: z.string().nullable(),
  latitude: z.number().min(-90).max(90).nullable(), longitude: z.number().min(-180).max(180).nullable(),
  startsAt: z.string().nullable(), endsAt: z.string().nullable(), categoryCode: z.string().nullable(),
  placeId: z.string().uuid().nullable(), officialUrl: publicUrl.nullable(),
  descriptionCandidate: z.string().max(300).nullable(), imageUrlCandidate: publicUrl.nullable(), fandomCandidate: z.string().nullable(),
}).strict();
export const externalCandidateSchema = z.object({
  id: z.string().uuid(), sourceId: z.string().uuid(), sourceName: z.string(), sourceType: z.string(), tier: z.enum(["A", "B", "C", "D"]),
  sourceUrl: publicUrl, placeName: z.string().nullable(),
  /* 중복 연결 후보의 이름. UUID 만 보여 주면 운영자가 고를 근거가 없습니다. */
  matchLabels: z.record(z.string(), z.string()).default({}), candidateType: z.enum(["PLACE", "EVENT", "LIVE_UPDATE", "OTHER"]), values: candidateValuesSchema,
  status: z.enum(["NEW", "NORMALIZED", "MATCHED", "AUTO_APPROVED", "NEEDS_REVIEW", "PUBLISHED", "DUPLICATE", "REJECTED", "STALE", "SOURCE_REMOVED", "ERROR"]),
  confidence: z.number().min(0).max(1), reviewReason: z.string().nullable(),
  /* `truncated` 는 같은 이름이 상한을 넘어 **일부만 비교했다**는 뜻입니다. 없으면 전부 봤습니다. */
  match: z.object({ kind: z.enum(["NO_MATCH", "POSSIBLE_MATCH", "HIGH_CONFIDENCE_MATCH", "EXACT_EXTERNAL_REFERENCE"]), ids: z.array(z.string().uuid()), confidence: z.number(), truncated: z.number().int().optional() }),
  changes: z.array(z.object({ field: z.string(), old: z.unknown(), new: z.unknown() })),
  collectedAt: z.string(), lastVerifiedAt: z.string(),
  sourcePublishedAt: z.string().datetime({ offset: true }).nullable(), sourceUpdatedAt: z.string().datetime({ offset: true }).nullable(),
  expiresAt: z.string(), version: z.number().int(),
});
export type ExternalCandidate = z.infer<typeof externalCandidateSchema>;
export const externalInfoSchema = z.object({
  candidateId: z.string().uuid(), title: z.string().nullable(), sourceName: z.string(), sourceUrl: publicUrl,
  lastVerifiedAt: z.string(), reviewStatus: z.literal("검수 중"), label: z.literal("외부 정보"), confidence: z.number(),
}).strict();

/** 운영자 화면이 읽는 출처 상태입니다. 값은 backend `rights`·건강 상태와 1:1 입니다. */
export const grantSchema = z.enum(["ALLOWED", "DENIED", "REVIEW"]);
export const ingestionSourceSchema = z.object({
  id: z.string().uuid(), name: z.string(), adapter: z.string(),
  tier: z.enum(["A", "B", "C", "D"]), enabled: z.boolean(),
  /** 검수 전 후보를 일반 사용자에게 보여주는지. 표시 권리와 **함께** 있어야 실제로 보입니다. */
  previewEnabled: z.boolean(),
  health: z.object({
    consecutiveFailures: z.number().int(),
    lastFailureKind: z.enum(["RETRYABLE", "NON_RETRYABLE", "RATE_LIMITED"]).nullable(),
    lastError: z.string().nullable(),
    lastSuccessAt: z.string().nullable(),
    nextAttemptAt: z.string().nullable(),
    intervalSeconds: z.number().int(),
    running: z.boolean(),
  }),
  /* 권리는 일곱 개가 모두 옵니다. 빠진 것을 허용으로 읽지 않도록 필수로 둡니다. */
  rights: z.object({
    collectionAllowed: grantSchema, displayAllowed: grantSchema,
    redistributionAllowed: grantSchema, imageDisplayAllowed: grantSchema,
    imageRehostAllowed: grantSchema, rawRetentionAllowed: grantSchema,
    autoPublishAllowed: grantSchema,
  }),
});
export type IngestionSource = z.infer<typeof ingestionSourceSchema>;
